import React from 'react';
import { Section, Typography, Grid, Alert, Button, Card } from 'components/base';
import withAuth from 'ecommerce/hoc/withAuth';
import useMyCreations from 'ecommerce/hooks/useMyCreations';
import useUserContext from 'ecommerce/context/user';
import useModalContext from 'components/context/modal';
import { formatDateTime } from 'utils/format';
import useEditorContext from 'editor/context/editorContext';
import renderArray from 'utils/renderArray';
import useDataContext from 'data/context/data';
import uuid from 'utils/uuid';
import { useRouter } from 'next/router';
import syncApi from 'ecommerce/api/sync';
import Page from 'editor/components/page/preview';
import { animateScroll } from 'react-scroll';
import { parseData } from 'editor-utils/dataParser';

import { getEditorPageLink, UserPageLink } from '../../components/pageLinks';
import { Head, Layout } from '../../components/layout';
import UserProjectsContainer, {
  PROJECT_SECTION,
} from '../../components/custom/userProjectsContainer';
import SignInPage from '../signin';

const UserCreationsPage = () => {
  const modalContext = useModalContext();
  const userContext = useUserContext();
  const editorContext = useEditorContext();
  const dataContext = useDataContext();
  const router = useRouter();

  const myCreations = useMyCreations(userContext.accessToken, [], item => {
    const product = dataContext.getSkuProduct(item.productId);

    if (product) {
      const template = {
        id: product.id,
        assetRootUrl: product.templateRootPath.replace(
          `/${product.version}`,
          `/${item.data.templateVersion}`,
        ),
        version: item.data.templateVersion,
        cover: item.data.data.cover ? [] : undefined,
        pages: item.data.data.pages ? [] : undefined,
      };

      const { pages, photos } = parseData(template, item.data.data, {
        onlyFirstPage: true,
      });

      if (pages.length > 0) {
        return {
          ...item,
          product,
          itemId: uuid(),
          preview: {
            page: { ...pages[0], visible: true },
            photos,
            template,
          },
        };
      }
    }
    return null;
  });

  const handleOnDeleteDraftClick = async draftId => {
    modalContext.confirm('Do you really want to remove your creation?', {
      title: 'Remove creation?',
      onConfirm: async () => {
        await myCreations.deleteCreation(draftId);
      },
    });
  };

  const handleOnDuplicateCreation = async draftId => {
    const { data, error } = await syncApi(userContext.accessToken).duplicateProductDraft(draftId);

    if (error) {
      modalContext.toast('Oops, something went wrong.', { type: 'warning' });
      console.error(error);
      return;
    }

    if (data) {
      modalContext.toast(`Creation #${data.id} has been created.`, { type: 'success' });
      animateScroll.scrollToTop();
    }

    await myCreations.reload();
  };

  const handleOnEditDraftClick = async (draftId, product) => {
    const { data: draft, error } = await syncApi(editorContext.accessToken).getProductDraft(
      draftId,
    );

    if (error) {
      modalContext.toast('Oops, something went wrong.', { type: 'warning' });
      console.error(error);
      return;
    }

    const version = draft.data.templateVersion;
    const draftName = draft.data.draftName;
    const draftLastModified = draft.updated_at;
    const data = draft.data.data;

    const template = {
      assetRootUrl: product.templateRootPath.replace(
        `/${product.version}`,
        `/${draft.data.templateVersion}`,
      ),
      version: draft.data.templateVersion,
    };

    const itemId = uuid();
    editorContext.editDraft(
      itemId,
      draft.productId,
      draftId,
      draftName,
      draftLastModified,
      template,
      data,
    );

    const { href, as } = getEditorPageLink(draft.productId, version, itemId);
    await router.push(href, as);
  };

  return (
    <>
      <Head title="My Projects - My Creations" />

      <Layout>
        <Section>
          <UserProjectsContainer
            section={PROJECT_SECTION.CREATIONS}
            loading={myCreations.isLoading}
          >
            <Alert type="warning">
              Creations that have already been ordered will not be listed here. You can reorder them
              in the <UserPageLink section="orders">My Account / Orders</UserPageLink> section.
            </Alert>
            {renderArray(
              myCreations.data,
              callback => (
                <>
                  <Card>
                    <Card.Body>
                      {callback(draft => (
                        <Grid.Row key={draft.id}>
                          <Grid.Col xs={3}>
                            <Page
                              key={draft.id}
                              page={draft.preview.page}
                              photos={draft.preview.photos}
                              template={draft.preview.template}
                              serverSideDPI={32}
                              style={{
                                maxWidth: 284,
                                maxHeight: 220,
                              }}
                              className="transition-none"
                            />
                          </Grid.Col>
                          <Grid.Col xs={9} className="d-flex flex-column">
                            <Typography.H3>
                              {draft.data.draftName || 'Unnamed Creation'}
                            </Typography.H3>
                            <Typography.H4>{draft.product.name}</Typography.H4>
                            <div style={{ flex: 1 }}>
                              <table className="mb-1">
                                <tr>
                                  <td className="pr-1 text-light">Creation Identifier</td>
                                  <td>
                                    <strong>#{draft.id}</strong>
                                  </td>
                                </tr>
                                {draft.data?.data?.pages?.length > 1 && (
                                  <tr>
                                    <td className="pr-1 text-light">Number of Pages</td>
                                    <td>
                                      <strong>{draft.data.data.pages.length}</strong>
                                    </td>
                                  </tr>
                                )}
                                <tr>
                                  <td className="pr-1 text-light">Created</td>
                                  <td>
                                    <strong>{formatDateTime(draft.created_at)}</strong>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="pr-1 text-light">Last Modified</td>
                                  <td>
                                    <strong>{formatDateTime(draft.updated_at)}</strong>
                                  </td>
                                </tr>
                              </table>
                            </div>
                            <div>
                              <Button
                                type="primary"
                                size="sm"
                                onClick={() => handleOnEditDraftClick(draft.id, draft.product)}
                                className="min-width-90"
                              >
                                Edit
                              </Button>{' '}
                              <Button
                                type="secondary"
                                size="sm"
                                onClick={() => handleOnDuplicateCreation(draft.id)}
                                className="min-width-90"
                              >
                                Duplicate
                              </Button>{' '}
                              <Button
                                type="danger"
                                size="sm"
                                onClick={() => handleOnDeleteDraftClick(draft.id)}
                                className="min-width-90"
                              >
                                Delete
                              </Button>
                            </div>
                          </Grid.Col>
                        </Grid.Row>
                      ))}

                      {myCreations.hasNextPage && (
                        <div className="text-center">
                          <hr />
                          <Button type="primary" onClick={myCreations.loadMore} className="my-5">
                            Load More
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </>
              ),
              <hr />,
              <Alert type="warning">No saved creations.</Alert>,
            )}
          </UserProjectsContainer>
        </Section>
      </Layout>
    </>
  );
};

export default withAuth(UserCreationsPage, SignInPage);
